import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle2, AlarmClock, Bell, BellOff, User } from "lucide-react";

const DELAY_MIN = 15;
const elapsed = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 60000);

let _audioCtx = null;
export function playAlarm(freq = 880, times = [0, 0.25, 0.5]) {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    times.forEach((t) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.18);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + 0.2);
    });
  } catch (e) {}
}

function OrderCard({ b, onDone, busy }) {
  const mins = elapsed(b.created_at);
  const delayed = mins >= DELAY_MIN;
  const timerCls = delayed ? "bg-rose-600" : mins >= 8 ? "bg-amber-500" : "bg-emerald-600";
  return (
    <div className={`bg-white rounded-xl border-2 shadow-sm flex flex-col ${delayed ? "border-rose-500 ring-2 ring-rose-400/60" : "border-gold/25"}`} data-testid={`kds-card-${b.id}`}>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-t-lg ${delayed ? "bg-rose-50" : "bg-cream-muted"}`}>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-forest-deep text-lg leading-tight" data-testid={`kds-table-${b.id}`}>Маса {b.table_number}</div>
          <div className="flex items-center gap-1 text-sm font-semibold text-gold-dark truncate" data-testid={`kds-waiter-${b.id}`}>
            <User className="w-3.5 h-3.5 shrink-0" />{b.waitress_name}
          </div>
        </div>
        <div className={`${timerCls} text-white px-2.5 py-1 rounded-lg font-bold text-lg flex items-center gap-1 tabular-nums`} data-testid={`kds-timer-${b.id}`}>
          <Clock className="w-4 h-4" />{mins}′
        </div>
      </div>
      {delayed && (
        <div className="px-3 pt-1 flex items-center gap-1 text-rose-600 font-bold text-xs" data-testid={`kds-delayed-${b.id}`}>
          <AlarmClock className="w-3.5 h-3.5" /> ЗАБАВЕНА
        </div>
      )}
      <div className="px-3 py-2 space-y-1.5 flex-1">
        {b.kitchen_items.map((it) => (
          <div key={it.id} className="leading-snug">
            <div className="text-base font-semibold text-forest-deep">
              <span className="inline-block min-w-[2ch] text-gold-dark">{it.quantity}×</span> {it.name_bg || it.name}
            </div>
            {it.note && <div className="text-sm text-rose-600 font-semibold pl-6">! {it.note}</div>}
          </div>
        ))}
      </div>
      <div className="p-2">
        <Button onClick={() => onDone(b.id)} disabled={busy} data-testid={`kds-done-${b.id}`}
          className="w-full h-12 text-lg font-bold bg-green-600 hover:bg-green-700 text-white rounded-lg gap-2">
          <CheckCircle2 className="w-5 h-5" /> ГОТОВО
        </Button>
      </div>
    </div>
  );
}

export default function KDS() {
  const [batches, setBatches] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [, setTick] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const batchesRef = useRef([]);
  const soundRef = useRef(true);
  const knownRef = useRef(null);
  batchesRef.current = batches;
  soundRef.current = soundOn;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kds/orders");
      const ids = new Set(data.map((b) => b.id));
      if (knownRef.current && data.some((b) => !knownRef.current.has(b.id)) && soundRef.current) playAlarm(660, [0, 0.2]);
      knownRef.current = ids;
      setBatches(data);
    } catch (e) { console.error("KDS load failed:", apiError(e)); }
  }, []);

  const delayedCount = batches.filter((b) => elapsed(b.created_at) >= DELAY_MIN).length;

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    const tk = setInterval(() => setTick((t) => t + 1), 10000);
    const alarm = setInterval(() => {
      if (soundRef.current && batchesRef.current.some((b) => elapsed(b.created_at) >= DELAY_MIN)) playAlarm();
    }, 20000);
    return () => { clearInterval(iv); clearInterval(tk); clearInterval(alarm); };
  }, [load]);

  const onDone = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/kds/batch/${id}/ready`);
      setBatches((bs) => bs.filter((b) => b.id !== id));
      toast.success("Сервитьорът е известен");
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  return (
    <div className="p-3 lg:p-4 min-h-[calc(100vh-4rem)] flex flex-col" data-testid="kds-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h1 className="font-display text-2xl text-forest-deep">Кухня · <span className="text-gold-dark" data-testid="kds-count">{batches.length}</span> активни</h1>
        <div className="flex items-center gap-2">
          {delayedCount > 0 && (
            <div className="flex items-center gap-2 bg-rose-600 text-white px-3 py-1.5 rounded-lg font-bold text-sm animate-pulse" data-testid="kds-delayed-banner">
              <AlarmClock className="w-4 h-4" /> {delayedCount} забавени (над {DELAY_MIN} мин)
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => { setSoundOn((s) => !s); if (!soundOn) playAlarm(); }}
            className="rounded-lg gap-1" data-testid="kds-sound-toggle">
            {soundOn ? <><Bell className="w-4 h-4" /> Звук вкл.</> : <><BellOff className="w-4 h-4" /> Звук изкл.</>}
          </Button>
        </div>
      </div>
      <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(230px,1fr))]" data-testid="kds-grid">
        {batches.map((b) => <OrderCard key={b.id} b={b} onDone={onDone} busy={busyId === b.id} />)}
      </div>
      {batches.length === 0 && <div className="flex-1 flex items-center justify-center text-muted-foreground text-xl" data-testid="kds-empty">Няма активни поръчки</div>}
    </div>
  );
}
